import styles from "./Modals.module.scss";
import React, { useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { observer } from "mobx-react-lite";
import { LLMProviderSharedFields } from "./ApiKeyProviderModal";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";

const BASE_DATA: Destination = {
  type: "bedrock" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.LLM_PROVIDER,
  settings: { region: "", accessKeyId: "", secretAccessKey: "", sessionToken: "", enabledModels: [], defaultModel: "" },
};

export const BedrockModalForm: React.FC<DestinationFormProps<"bedrock">> = observer(({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDestinationDataType<"bedrock">>>(null);
  const initialValues = (settings || BASE_DATA.settings) as FormDestinationDataType<"bedrock">;
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(
    !initialValues.region || !initialValues.accessKeyId || !initialValues.secretAccessKey
  );

  const validate = async (values: FormDestinationDataType<"bedrock">) => {
    const errors: FormikErrors<PartialRecord<keyof FormDestinationDataType<"bedrock">, string>> = {};
    if (!values.region) {
      errors.region = t("field_is_required");
    }
    if (!values.accessKeyId) {
      errors.accessKeyId = t("field_is_required");
    }
    if (!values.secretAccessKey) {
      errors.secretAccessKey = t("field_is_required");
    }
    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDestinationDataType<"bedrock">;
    return { ...BASE_DATA, settings: data };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  return (
    <>
      <FormElement initialValues={initialValues} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.aws_region")} />
          <FormTextInput name={"region"} paddingInline="none" placeholder="us-east-1" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.aws_access_key_id")} />
          <FormPasswordTextInput name={"accessKeyId"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.aws_secret_access_key")} />
          <FormPasswordTextInput name={"secretAccessKey"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.aws_session_token")} />
          <FormPasswordTextInput name={"sessionToken"} paddingInline="none" />
        </div>
        <LLMProviderSharedFields providerType="bedrock" formikContext={formikContext} settings={settings} />
        <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
      </FormElement>
    </>
  );
});
